import React, { useState, useEffect } from 'react';

// Helper function to get initial state from sessionStorage
const getSessionStorageItem = (key, defaultValue) => {
    try {
        const storedValue = sessionStorage.getItem(key);
        if (storedValue) {
            return JSON.parse(storedValue);
        }
    } catch (error) {
        console.error(`Error parsing sessionStorage key '${key}':`, error);
        sessionStorage.removeItem(key);
    }
    return defaultValue;
};

function ClientForm({ clientToEdit, onSave, onCancel, API_URL }) {
    // Initialize state with data from sessionStorage or default values
    const [client, setClient] = useState(() => getSessionStorageItem('clientFormData', {
        name: '',
        customerId: '',
        address: {
            street: '',
            city: '',
            state: '',
            pincode: ''
        },
        serviceDescription: 'Digital Marketing Service Charges for',
        amount: '',
        pendingAmount: '',
        bankDetails: {
            accountHolder: 'ZAKIYA BANU',
            accountNumber: '50100380743330',
            ifsc: 'HDFC0001237',
            Branch: 'BROADWAY PARRYS CORNER',
            accountType: 'SAVING'
        }
    }));
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');

    // Effect to handle editing a client and override session storage data
    useEffect(() => {
        if (clientToEdit) {
            // Ensure missing nested objects (address, bankDetails) are initialized
            const defaultState = getSessionStorageItem('clientFormData', {
                name: '',
                customerId: '',
                address: {
                    street: '',
                    city: '',
                    state: '',
                    pincode: ''
                },
                serviceDescription: 'Digital Marketing Service Charges for',
                amount: '',
                pendingAmount: '',
                bankDetails: {
                    accountHolder: 'ZAKIYA BANU',
                    accountNumber: '50100380743330',
                    ifsc: 'HDFC0001237',
                    Branch: 'BROADWAY PARRYS CORNER',
                    accountType: 'SAVING'
                }
            });

            setClient({
                ...defaultState,
                ...clientToEdit,
                address: {
                    ...defaultState.address,
                    ...(clientToEdit.address || {})
                },
                bankDetails: {
                    ...defaultState.bankDetails,
                    ...(clientToEdit.bankDetails || {})
                },
                pendingAmount: clientToEdit.pendingAmount || ''
            });
        } else {
            // Only reset to defaults if no clientToEdit is present
            setClient(prevClient => getSessionStorageItem('clientFormData', {
                name: '',
                customerId: '',
                address: {
                    street: '',
                    city: '',
                    state: '',
                    pincode: ''
                },
                serviceDescription: 'Digital Marketing Service Charges for',
                amount: '',
                pendingAmount: '',
                bankDetails: {
                    accountHolder: 'ZAKIYA BANU',
                    accountNumber: '50100380743330',
                    ifsc: 'HDFC0001237',
                    Branch: 'BROADWAY PARRYS CORNER',
                    accountType: 'SAVING'
                }
            }));
        }
    }, [clientToEdit]);

    // Effect to save the form state to sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem('clientFormData', JSON.stringify(client));
    }, [client]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('address.')) {
            const addressField = name.split('.')[1];
            setClient(prevClient => ({
                ...prevClient,
                address: {
                    ...((prevClient && prevClient.address) || {}),
                    [addressField]: value
                }
            }));
        } else if (name.startsWith('bankDetails.')) {
            const bankDetailsField = name.split('.')[1];
            setClient(prevClient => ({
                ...prevClient,
                bankDetails: {
                    ...((prevClient && prevClient.bankDetails) || {}),
                    [bankDetailsField]: value
                }
            }));
        } else {
            setClient(prevClient => ({
                ...prevClient,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('Saving client...');
        setMessageType('info');

        try {
            const method = client._id ? 'PUT' : 'POST';
            const url = client._id ? `${API_URL}/clients/${client._id}` : `${API_URL}/clients`;

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(client),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const savedClient = await response.json();
            setMessage(`Client ${client._id ? 'updated' : 'added'} successfully!`);
            setMessageType('success');
            setTimeout(() => {
                onSave(savedClient);
                // Clear sessionStorage on successful save
                sessionStorage.removeItem('clientFormData');
            }, 1000);
        } catch (error) {
            console.error('Error saving client:', error);
            setMessage(`Error saving client: ${error.message}`);
            setMessageType('error');
        }
    };

    return (
        <div className="w-full">
            <h2 className="text-3xl font-extrabold text-center text-indigo-300 mb-8 drop-shadow-lg">
                {clientToEdit ? 'Edit Client Details' : 'Add New Client'}
            </h2>
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                {/* Client Name */}
                <div>
                    <label htmlFor="name" className="text-lg font-medium text-gray-300 mb-1 block">Client Name:</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={client.name}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                        placeholder="e.g., New Client Co."
                        required
                    />
                </div>

                {/* Customer ID */}
                <div>
                    <label htmlFor="customerId" className="text-lg font-medium text-gray-300 mb-1 block">Customer ID:</label>
                    <input
                        type="text"
                        id="customerId"
                        name="customerId"
                        value={client.customerId}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                        placeholder="e.g., CUS123NEW"
                        required
                    />
                </div>

                {/* Amount */}
                <div>
                    <label htmlFor="amount" className="text-lg font-medium text-gray-300 mb-1 block">Default Amount (INR):</label>
                    <input
                        type="number"
                        id="amount"
                        name="amount"
                        value={client.amount}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                        placeholder="e.g., 2500.00"
                        step="0.01"
                        required
                    />
                </div>

                {/* New Input for Pending Amount */}
                <div>
                    <label htmlFor="pendingAmount" className="text-lg font-medium text-gray-300 mb-1 block">Pending Amount (INR):</label>
                    <input
                        type="number"
                        id="pendingAmount"
                        name="pendingAmount"
                        value={client.pendingAmount}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                        placeholder="e.g., 1000.00 (optional)"
                        step="0.01"
                    />
                </div>

                {/* Service Description */}
                <div>
                    <label htmlFor="serviceDescription" className="text-lg font-medium text-gray-300 mb-1 block">Service Description:</label>
                    <input
                        type="text"
                        id="serviceDescription"
                        name="serviceDescription"
                        value={client.serviceDescription}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                        placeholder="e.g., Digital Marketing Service Charges for"
                    />
                </div>

                <h3 className="text-2xl font-semibold text-indigo-400 mt-6 mb-2">Address Details:</h3>
                {/* Address Fields */}
                <div>
                    <label htmlFor="address.street" className="text-base font-medium text-gray-400 mb-1 block">Street:</label>
                    <input
                        type="text"
                        id="address.street"
                        name="address.street"
                        value={(client.address && client.address.street) || ''}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                        placeholder="e.g., 123 Main St"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="address.city" className="text-base font-medium text-gray-400 mb-1 block">City:</label>
                    <input
                        type="text"
                        id="address.city"
                        name="address.city"
                        value={(client.address && client.address.city) || ''}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                        placeholder="e.g., Chennai"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="address.state" className="text-base font-medium text-gray-400 mb-1 block">State:</label>
                    <input
                        type="text"
                        id="address.state"
                        name="address.state"
                        value={(client.address && client.address.state) || ''}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                        placeholder="e.g., TN"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="address.pincode" className="text-base font-medium text-gray-400 mb-1 block">Pincode:</label>
                    <input
                        type="text"
                        id="address.pincode"
                        name="address.pincode"
                        value={(client.address && client.address.pincode) || ''}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                        placeholder="e.g., 600001"
                        required
                    />
                </div>

                <h3 className="text-2xl font-semibold text-indigo-400 mt-6 mb-2">Bank Details:</h3>
                {/* Bank Details Fields (pre-filled but editable) */}
                <div>
                    <label htmlFor="bankDetails.accountHolder" className="text-base font-medium text-gray-400 mb-1 block">Account Holder:</label>
                    <input
                        type="text"
                        id="bankDetails.accountHolder"
                        name="bankDetails.accountHolder"
                        value={(client.bankDetails && client.bankDetails.accountHolder) || ''}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="bankDetails.accountNumber" className="text-base font-medium text-gray-400 mb-1 block">Account Number:</label>
                    <input
                        type="text"
                        id="bankDetails.accountNumber"
                        name="bankDetails.accountNumber"
                        value={(client.bankDetails && client.bankDetails.accountNumber) || ''}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="bankDetails.ifsc" className="text-base font-medium text-gray-400 mb-1 block">IFSC Code:</label>
                    <input
                        type="text"
                        id="bankDetails.ifsc"
                        name="bankDetails.ifsc"
                        value={(client.bankDetails && client.bankDetails.ifsc) || ''}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="bankDetails.accountType" className="text-base font-medium text-gray-400 mb-1 block">Account Type:</label>
                    <input
                        type="text"
                        id="bankDetails.accountType"
                        name="bankDetails.accountType"
                        value={(client.bankDetails && client.bankDetails.accountType) || ''}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                        required
                    />
                </div>
                {/* New input for Bank Branch */}
                <div>
                    <label htmlFor="bankDetails.Branch" className="text-base font-medium text-gray-400 mb-1 block">Branch:</label>
                    <input
                        type="text"
                        id="bankDetails.Branch"
                        name="bankDetails.Branch"
                        value={(client.bankDetails && client.bankDetails.Branch) || ''}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                        required
                    />
                </div>

                <div className="flex justify-end gap-4 mt-8">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="py-3 px-6 rounded-xl font-bold text-lg text-white bg-gray-600 hover:bg-gray-700 transition-colors duration-300 shadow-lg"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="py-3 px-6 rounded-xl font-bold text-lg text-white
                            bg-gradient-to-r from-green-500 to-teal-600
                            hover:from-green-600 hover:to-teal-700
                            focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800
                            transition-all duration-300 ease-in-out shadow-lg"
                    >
                        {clientToEdit ? 'Update Client' : 'Add Client'}
                    </button>
                </div>

                {message && (
                    <p className={`mt-5 text-center text-lg rounded-xl px-4 py-3 ${
                        messageType === 'success'
                            ? 'bg-green-900 text-green-300'
                            : messageType === 'error'
                                ? 'bg-red-900 text-red-300'
                                : 'bg-indigo-900 text-indigo-300'
                    } transition-all duration-300`}>
                        {message}
                    </p>
                )}
            </form>
        </div>
    );
}

export default ClientForm;